import { required, requiredIf } from 'vuelidate/lib/validators';
import { cloneDeep, isEmpty, isUndefined } from 'lodash';
import Property from '@/services/Property/Property';
import ClientActionType from '@/services/Action/ClientActionType';
import ClientActionCategory from '@/services/Action/ClientActionCategory';

const isUndefinedOrEmpty = value => isUndefined(value) || isEmpty(value);

export const propertyForm = () => cloneDeep({
  propertyId: {
    label: 'Select property',
    type: 'select',
    labelBy: 'label',
    trackBy: 'propertyId',
    value: null,
    options: [],
    optionsFn: async () => {
      const formatAddresses = (propertyRow) => {
        const validatedStreetNumber = propertyRow.streetNumber || '';
        const validatedStreet = propertyRow.street || '';

        return `${propertyRow.uprn} - ${[
          propertyRow.numberRange,
          propertyRow.buildingName,
          `${validatedStreetNumber} ${validatedStreet}`,
          propertyRow.town,
          propertyRow.postcode,
        ].filter(Boolean).join(', ')}`;
      };

      const { data } = await Property.all({
        params: {
          limit: 5000,
          columns: [
            'propertyId',
            'uprn',
            'streetNumber',
            'street',
            'numberRange',
            'buildingName',
            'town',
            'postcode',
          ]
        }
      });

      return data.data.map(item => ({ ...item, label: formatAddresses(item) }));
    },
  },
  linkedActionId: {
    label: 'Related action',
    type: 'select',
    labelBy: 'label',
    trackBy: 'linkedActionId',
    value: null,
    options: [],
    disabled: true,
  },
  linkedActionPriority: {
    label: 'Priority',
    type: 'text',
    value: '',
    disabled: true,
    hidden: form => (!form.linkedActionId.value),
  },
  linkedActionDueDate: {
    label: 'Due Date',
    type: 'text',
    value: '',
    disabled: true,
    hidden: form => (!form.linkedActionId.value),
  },
  linkedActionStatus: {
    label: 'Status',
    type: 'text',
    value: '',
    disabled: true,
    hidden: form => (!form.linkedActionId.value),
  },
  complianceTypeId: {
    label: 'Compliance',
    type: 'select',
    labelBy: 'assessmentType',
    disabled: true,
    trackBy: 'assessmentTypeId',
    value: null,
    options: [],
    hidden: form => (!!form.linkedActionId.value),
  },
  assessmentTypeId: {
    label: 'Assessment Type',
    type: 'select',
    labelBy: 'assessmentType',
    disabled: true,
    trackBy: 'assessmentTypeId',
    value: null,
    options: [],
    hidden: form => (!!form.linkedActionId.value),
  },
});


const makeCategoryFields = (withConfirm = false) => {
  const categoryFields = {};
  for (let i = 0; i < ClientActionCategory.MAX_DEPTH(); i += 1) {
    const categoryKey = `clientActionCategoryId-${i}`;
    const parentCategoryKey = `clientActionCategoryId-${i - 1}`;

    const isParent = i === 0;
    // We hide it if the previous category doesnt have a value.
    // Top level (depth 0) never hides.
    const hidden = isParent
      ? false
      : form => (form[parentCategoryKey].value === null);

    const label = `Category ${i + 1}`;

    categoryFields[categoryKey] = {
      label,
      type: 'select',
      labelBy: 'clientActionCategory',
      trackBy: 'clientActionCategoryId',
      disabled: false,
      value: null,
      options: [],
      hidden,
    };
  }

  return categoryFields;
};

export const categoryForm = () => cloneDeep(makeCategoryFields(true));

export const actionForm = () => cloneDeep({
  ...makeCategoryFields(),
  clientActionTypeId: {
    label: 'Type',
    type: 'select',
    labelBy: 'clientActionType',
    trackBy: 'clientActionTypeId',
    disabled: false,
    value: null,
    options: [],
    optionsFn: async () => {
      const { data } = await ClientActionType.list();
      return data.data;
    },
  },
  clientActionPriorityId: {
    label: 'Priority',
    type: 'select',
    labelBy: 'clientActionPriority',
    trackBy: 'clientActionPriorityId',
    disabled: true,
    value: null,
    options: [],
    optionsFn: null,
  },
  dueDate: {
    label: 'Due date',
    type: 'date',
    value: null,
  },
});

export const actionDetailForm = () => cloneDeep({
  description: {
    label: 'Description',
    type: 'textarea',
    value: '',
  },
  recommendation: {
    label: 'Recommendation',
    type: 'textarea',
    value: '',
  },
  specification: {
    label: 'Specification',
    type: 'textarea',
    value: '',
  },
  rate: {
    label: 'Rate',
    type: 'number',
    value: '',
  },
  minQuantity: {
    label: 'Min Qty',
    type: 'number',
    value: '',
    columnOptions: {
      md: '6',
    },
  },
  maxQuantity: {
    label: 'Max Qty',
    type: 'number',
    value: '',
    columnOptions: {
      md: '6',
    },
  },
  minCost: {
    label: 'Min Cost',
    type: 'number',
    value: '',
    columnOptions: {
      md: '6',
    },
    disabled: true,
  },
  maxCost: {
    label: 'Max Cost',
    type: 'number',
    value: '',
    columnOptions: {
      md: '6',
    },
    disabled: true,
  },
});

export const photosForm = () => cloneDeep({
  photos: {
    id: 'photos',
    label: 'Photos',
    type: 'file',
    fileUrl: `${api.defaults.baseURL}/actions/client`,
    options: {
      acceptedFiles: '.jpg,.jpeg,.png',
    },
  },
});


export const propertyFormValidators = {
  complianceTypeId: {
    requiredIf: requiredIf(function () {
      return isUndefinedOrEmpty(this.$v.fields.linkedActionId.value.$model);
    }),
  },
  propertyId: {
    required,
  },
};

export const actionFormValidators = {
  clientActionPriorityId: {
    required,
  },
};

const greaterThanZero = value => (value > 0) || isUndefinedOrEmpty(value);
export const actionDetailsFormValidators = {
  description: {
    required,
  },
  rate: {
    requiredIf: requiredIf(function () {
      return !isUndefinedOrEmpty(this.$v.fields.minQuantity.value.$model)
        || !isUndefinedOrEmpty(this.$v.fields.maxQuantity.value.$model);
    }),
    minValue: greaterThanZero,
  },
  minQuantity: {
    requiredIf: requiredIf(function () {
      return !isUndefinedOrEmpty(this.$v.fields.rate.value.$model);
    }),
    minValue: greaterThanZero,
  },
  maxQuantity: {
    requiredIf: requiredIf(function () {
      return !isUndefinedOrEmpty(this.$v.fields.rate.value.$model);
    }),
    minValue: greaterThanZero,
  },
};
